import { InvitationStatus } from '@compass/client/types/organization';
import { formatDistanceToNow } from 'date-fns';
import { Copy, Mail, MoreVertical, Trash2 } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { InvitationStatusBadge } from '@/components/InvitationStatusBadge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useDeleteOrganizationInvitation,
  useOrganizationInvitations,
} from '@/hooks/useOrganizationInvitations';

interface PendingInvitationsProps {
  organizationId: string;
}

export const PendingInvitations: React.FC<PendingInvitationsProps> = ({ organizationId }) => {
  // Fetch all invitations (pending, expired, revoked) - excluding only accepted invitations
  const { data, isLoading, error } = useOrganizationInvitations(organizationId, {
    limit: 100,
    // Note: Omitting status filter will fetch all invitations
    // Once the backend API supports multiple statuses, we can filter for:
    statuses: [
      InvitationStatus.INVITATION_STATUS_PENDING,
      InvitationStatus.INVITATION_STATUS_EXPIRED,
    ],
  });
  const deleteMutation = useDeleteOrganizationInvitation();

  // Process invitations to override status to expired if expiration time has passed
  const invitations = React.useMemo(() => {
    return data?.invitations?.map(invitation => {
      const expiresAt = invitation.expires_at ? new Date(invitation.expires_at) : null;
      const hasExpired = expiresAt ? expiresAt < new Date() : false;

      // Override status to expired if the invitation has expired but status is still pending
      if (hasExpired && invitation.status === InvitationStatus.INVITATION_STATUS_PENDING) {
        return {
          ...invitation,
          status: InvitationStatus.INVITATION_STATUS_EXPIRED,
        };
      }

      return invitation;
    });
  }, [data?.invitations]);

  const handleDelete = async (invitationId: string, email: string) => {
    try {
      await deleteMutation.mutateAsync({
        organization_id: organizationId,
        invitation_id: invitationId,
      });
      toast.success(`Invitation to ${email} has been cancelled`);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to cancel invitation',
      );
    }
  };

  const handleCopyInviteUrl = async (invitationUrl: string | undefined, email: string) => {
    if (!invitationUrl) {
      toast.error('Invitation URL not available');
      return;
    }

    try {
      await navigator.clipboard.writeText(invitationUrl);
      toast.success(`Invitation URL copied to clipboard for ${email}`);
    } catch {
      toast.error('Failed to copy invitation URL');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-64" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
        <p className="text-sm text-destructive">Failed to load pending invitations</p>
        <p className="mt-1 text-xs text-muted-foreground">{(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {!invitations || invitations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <Mail className="h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">No pending invitations</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              invitations?.map(invitation => {
                const expiresAt = invitation.expires_at ? new Date(invitation.expires_at) : null;
                const isExpired = expiresAt ? expiresAt < new Date() : false;
                const isAccepted =
                  invitation.status === InvitationStatus.INVITATION_STATUS_ACCEPTED;

                return (
                  <TableRow key={invitation.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{invitation.invitee_email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <InvitationStatusBadge status={invitation.status} />
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-xs">
                          {invitation.created_at
                            ? formatDistanceToNow(new Date(invitation.created_at), {
                                addSuffix: true,
                              })
                            : ''}
                        </div>
                        {expiresAt && !isAccepted ? (
                          <div
                            className={`text-xs ${isExpired ? 'text-destructive' : 'text-muted-foreground'}`}
                          >
                            {!isExpired
                              ? `Expires ${formatDistanceToNow(expiresAt, { addSuffix: true })}`
                              : ''}
                          </div>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              handleCopyInviteUrl(
                                invitation.invitation_url,
                                invitation.invitee_email || '',
                              )
                            }
                          >
                            <Copy className="mr-2 h-4 w-4" />
                            Copy Invite URL
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() =>
                              handleDelete(invitation.id || '', invitation.invitee_email || '')
                            }
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Cancel Invitation
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
